import React, { useState } from "react";

import { useForm, Controller } from "react-hook-form";

import Modal from "../Shared/Modal";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { useSocialPostMutations } from "../../data/social/useSocialPostMutations";
import { StyledRadioButtonGroupInput } from "../Shared/StyledInputs/StyledRadioButtonGroupInput";
import LoadingWheel from "../Shared/LoadingWheel";

interface ManualPropertyFormFields {
    propertyPostType: string;
    propertyMedia: string;
    asLinkPost?: boolean;
    numberOfImages?: string;
}

interface ManualPropertyPostModalProps {
    open: boolean;
    setOpen: () => void;
    propertyId: string;
    agencyId: string;
}

export const ManualPropertyPostModal = ({ open, setOpen, propertyId, agencyId }: ManualPropertyPostModalProps) => {
    const [isPosting, setIsPosting] = useState(false);
    const { create } = useSocialPostMutations(agencyId!);
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        control,
        formState: { errors }
    } = useForm<ManualPropertyFormFields>({
        defaultValues: {
            propertyPostType: "1",
            propertyMedia: "1",
            numberOfImages: "1",
            asLinkPost: true,
        },
    });

    const postTypeOpts = [
        { label: "A Reduction", value: "0" },
        { label: "A Sale/Let", value: "1" },
        { label: "A New Listing", value: "2" },
    ]

    const postMediaTypeOpts = [
        { label: "None", value: "0" },
        { label: "OG Video", value: "1", disabled: true },
        { label: "OG Image", value: "2" },
    ]

    const onSubmit = (data: ManualPropertyFormFields) => {
        setIsPosting(true);

        create.mutate({
            ...data,
            propertyId: propertyId,
            propertyPostType: parseInt(data.propertyPostType),
            numberOfImages: parseInt(data.numberOfImages || "0"),
            propertyMedia: parseInt(data.propertyMedia || "0"),
        }, {
            onSuccess: () => {
                setOpen();
            },
            onSettled: () => {
                setTimeout(() => { reset(); setIsPosting(false) }, 300);
            }
        });
    };

    const ensureCorrectNumberOfImages = (propertyMedia: string) => {
        let maxImages = 10

        if (propertyMedia === "2") {
            maxImages = 9;
        }

        const currentNumberOfImages = parseInt(watch("numberOfImages") || "1");

        if (currentNumberOfImages > maxImages) {
            setValue("numberOfImages", maxImages.toString());
        }
    }

    return (
        <Modal open={open} setOpen={setOpen} width={"max-w-xl"}>
            <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Create Social Post for Property
                </h3>
            </div>
            {isPosting ?
                <div className="flex flex-col w-full items-center mt-8 space-y-8 justify-center py-10">
                    <LoadingWheel width="w-12" height="w-12" />
                    <span className="text-etpink-600 font-medium text-lg">Posting...</span>
                </div>
                :
                <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
                    <StyledRadioButtonGroupInput
                        label="Post this property as"
                        options={postTypeOpts}
                        registerName="propertyPostType"
                        watch={watch}
                        setValue={setValue}
                    />
                    <div className="w-1/3">
                        <SwitchInput
                            label="Link Post"
                            registerName="asLinkPost"
                            register={register}
                            watch={watch}
                            setValue={setValue}
                        />
                    </div>

                    {watch("asLinkPost") === false && (
                        <div className="space-y-4">
                            <StyledRadioButtonGroupInput
                                label="Headline Media"
                                options={postMediaTypeOpts}
                                registerName="propertyMedia"
                                watch={watch}
                                setValue={setValue}
                                onChange={ensureCorrectNumberOfImages}
                            />
                            {watch("propertyMedia") !== "1" && (
                                <div>
                                    <label className="mb-1 text-sm font-medium text-gray-700">Number of Additional Property Images</label>
                                    {(() => {
                                        const maxImages = watch("propertyMedia") === "2" ? 9 : 10;

                                        return (
                                            <>
                                                <Controller
                                                    name="numberOfImages"
                                                    control={control}
                                                    defaultValue={"1"}
                                                    rules={{
                                                        validate: (val: any) => {
                                                            const n = parseInt(val || "0");
                                                            return n <= maxImages || `Maximum is ${maxImages}`;
                                                        }
                                                    }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            min={1}
                                                            max={maxImages}
                                                            className="mt-1 w-full block shadow-sm focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm border-gray-300 rounded-md"
                                                        />
                                                    )}
                                                />
                                                {errors.numberOfImages && (
                                                    <p className="text-xs text-red-500 mt-1">{(errors.numberOfImages as any).message}</p>
                                                )}
                                            </>
                                        )
                                    })()}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="!mt-8">
                        {/* @ts-ignore */}
                        <ButtonInput
                            label="Create Post"
                            isSubmit={true}
                            onClick={() => { }}
                        />
                    </div>
                </form>
            }
        </Modal>
    );
};